// R67 — **THE DELIVERED READING, AND THE FILE R48 SAID COULD NOT MOVE.**
//
// Every generated reading on this site is handed to a member through the two components at the
// bottom of this file. R48 derived them (thirteen surfaces had assembled the same four parts by
// hand, then four more were found without the lid) and left them in App.tsx, with a measured reason
// written beside them: `ClosableReport` holds four hooks and `ReportPrintPortal` calls
// `createPortal`, "which the gates' stub jsx-runtime has no answer for".
//
// **THAT PREMISE WAS MEASURED AGAIN AND IT IS FALSE, WHICH IS WHY THIS FILE EXISTS.** `loadTsx`
// stubs exactly two specifiers — `lucide-react` and `react/jsx-runtime` — and `react-dom` is not one
// of them: it resolves to the real package and bundles like any other dependency. And a hook is only
// a problem for a component a gate CALLS. The stub runtime turns `<ClosableReport>` into
// `{ type, props }` and never invokes it (R10-3's ruling, which is why `AcgPlanetGroups` came out
// holding three hooks), so the two derived blocks below are as callable out here as any leaf.
// The premise held for as long as nobody ran it.
//
// **WHAT THAT COSTS TO LEAVE IN PLACE IS NOT TIDINESS.** Four fixtures hold laws about this block
// and all four hold them by reading App.tsx AS TEXT: the delivery frame is one wrapper class
// (`r24`), every reading surface gets its print copy (`r41`), every reading still reaches the
// dispatcher (`r11`), and the block's own sentences are catalog rows (`item14`). A regex can see
// that `<ResultDestinations` is written inside `DeliveredReadingBody`; it cannot see what a call
// site actually receives. Out here a gate CALLS the pair once per state and reads the tree back.
// It also unblocks the rooms behind it — `CauldronExperience` could not move in R64 for exactly one
// reason, that it renders `DeliveredReading` and a component in `src/components` cannot import back
// out of App.tsx (R12's ruling).
//
// **THE TWO TAROT READERS ARE DELIBERATELY NOT IN THIS BLOCK** and must stay out: 14-B6a already
// declares they keep their OWN layout (the reader's bar, not the report's action row) and take only
// their SENTENCES from the one law.
//
// **AND IT IS TWO COMPONENTS BECAUSE THE MEASUREMENT SAID SO** (R48's second finding). The negative
// that holds the derivation — the block's own wrapper class appears ONCE — found four more copies of
// the INNER half: the four astrology/numerology reading MODALS, already inside a `ModalBackdrop` and
// so not to be wrapped in the dismissable shell a second time. They are not a different block, they
// are this block without its lid. `DeliveredReadingBody` is that; `DeliveredReading` is the lid.
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";
import { downloadReportPdf } from "../lib/downloads";
import { pdfAllowed } from "../readingPdf.js";
import { ReportBody, type AnyReport } from "./ReportBody";
import { ResultDestinations } from "./ResultDestinations";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav";

// Wraps any report rendered in place so it can be dismissed three ways: the X
// button, the Escape key, or a click outside the report. Site-wide affordance for
// every in-place reading (Find Your Type, tarot, and any future in-place report).
// A delivered, generated reading. COST-PROTECTION LAW (C2): a generated result is never
// dismissed by accident. Click-outside / background does NOTHING here (deliberately the
// reverse of the informational-popup rule). It closes ONLY via its X or Escape, and if it
// has not been saved, that close first asks to confirm ("Close without saving? You can find
// it in Your Reports") so a member never loses a reading they paid to generate. Pass
// `saved` once the member has saved it, and the close becomes immediate.
//
// R67 — **AND THE CONFIRM WAS THE LAST ENGLISH THING STANDING BETWEEN A SPANISH MEMBER AND HER
// READING.** `language` has been a prop here since 14-B6a and was read on exactly one line, the X
// button's accessible name; the four strings inside the alertdialog — the sentence, both buttons and
// the dialog's own label — were hand-typed English. So on a Spanish delivery the reading turns, the
// destinations row turns, the close button turns, and the ONE modal that can cost her the reading
// does not. That is 14-B6's own lesson inverted (a lone English box on a translated screen), and it
// is R38's trap exactly: a capability wired to nothing has no error state. Four rows, one read each.
export function ClosableReport({ onClose, saved, children, language = DEFAULT_LANGUAGE }: { onClose: () => void; saved?: boolean; children: ReactNode; language?: string }) {
  const [confirming, setConfirming] = useState(false);
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const attemptClose = useCallback(() => {
    if (savedRef.current) { onClose(); return; }
    setConfirming(true);
  }, [onClose]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // R116 — **`stopPropagation` DID NOT STOP THE ONE LISTENER THAT WAS ACTUALLY TAKING THIS
      // READING AWAY, AND THE COMMENT ABOVE HAS BEEN DESCRIBING A GUARANTEE IT DID NOT HAVE.**
      // `stopPropagation` ends the walk UP the tree; it does nothing at all to another listener
      // registered on the SAME node. `MagicOverlay` — the shell all seven Magic Room generators are
      // drawn inside — also listens on `document`, and a child's effects run before its parent's, so
      // on one Escape this handler raised the unsaved-close confirm and the shell then unmounted the
      // whole generator underneath it. The confirm C2 exists for was opened and destroyed in the same
      // keypress, over a working she had just paid a generation for. `stopImmediatePropagation` is
      // what the sentence below always meant: while a delivered reading is open, ONE press is
      // answered by the reading and by nothing else.
      event.stopImmediatePropagation();
      // Escape backs out of the confirm if it is open; otherwise it starts a close.
      setConfirming((c) => {
        if (c) return false;
        if (savedRef.current) { onClose(); return false; }
        return true;
      });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // OV2-9: a tab takes this with it too, and it goes WITHOUT the unsaved-close confirm. The confirm
  // exists to stop an accidental loss, and there is nothing to lose: the reading was shelved by the
  // server the moment it finished. Asking "close without saving?" over a navigation the member just
  // asked for would be the modal arguing with the tap, which is the whole thing she ruled against.
  useEffect(() => {
    const step = () => { setConfirming(false); onClose(); };
    window.addEventListener(CLOSE_OVERLAYS_EVENT, step);
    return () => window.removeEventListener(CLOSE_OVERLAYS_EVENT, step);
  }, [onClose]);
  return (
    <div className="closable-report">
      <button type="button" className="closable-report__close" aria-label={t(language, "reading.close")} onClick={attemptClose}>
        <X size={18} />
      </button>
      {children}
      {confirming ? (
        <div className="closable-report__confirm" role="alertdialog" aria-modal="true" aria-label={t(language, "reading.confirmCloseLabel")}>
          <div className="closable-report__confirm-card">
            <p>{t(language, "reading.confirmClose")}</p>
            <div className="closable-report__confirm-actions">
              <button type="button" className="btn-ghost" onClick={() => setConfirming(false)}>{t(language, "reading.keepOpen")}</button>
              <button type="button" className="btn-solid" onClick={() => { setConfirming(false); onClose(); }}>{t(language, "reading.closeAnyway")}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Print-only WHITE copy of the report, portalled to <body>. In print we hide the
// whole app (#root) and show only this, so the PDF is a clean white document with
// no blank pages (the old visibility:hidden approach left every hidden element
// occupying page space, which produced the blank pages).
export function ReportPrintPortal({ data }: { data: AnyReport }) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="report-print" aria-hidden="true">
      <ReportBody data={data} />
    </div>,
    document.body,
  );
}

// R41 — THE READING AND THE COPY THE PAPER GETS ARE ONE MOUNT. Measured: twenty surfaces render a
// delivered reading and TEN mounted the print copy, so the same reading printed as a clean white
// document in ten rooms and as the room around it in the other ten (Dream, Palmistry, Numerology,
// Find Your Type, every Magic Room generator). Nothing threw; it was decided by whether somebody
// remembered a second line, so there is none (R31: derive). The portal keeps `ReportBody`, not this.
export function ReportWithPrint({ data }: { data: AnyReport }) {
  return (<><ReportBody data={data} /><ReportPrintPortal data={data} /></>);
}

// R48 — **THE DELIVERED READING IS ONE BLOCK, AND IT WAS TYPED THIRTEEN TIMES.** R41's shape one
// level up: the dismissable shell, the reading with its print copy, the destinations row and the
// save note were four components a call site had to assemble in the right order, and thirteen rooms
// assembled them byte for byte identically — differing only in `room`, `extras` and (at two) the
// names of the state variables. All thirteen were UNIFORM when measured, which is R25's shape
// exactly: a law that is correct today and held by nothing, where the next room is the one that
// gets it wrong. The drift this house has already paid for on this very block is the argument
// (14-B6a: fourteen copies disabled Save on `saving` alone, so a second tap put a duplicate reading
// on her shelf) — the copies were derived onto `ResultDestinations` then, and the frame around it
// stayed hand-typed. It is derived now, so a new reading surface gets the whole delivery by
// rendering the reading.
export function DeliveredReadingBody({ report, room, language, save, saveNote, onSaveMyRoom, extras }: {
  report: AnyReport; room: string; language: string; save: string; saveNote: string;
  onSaveMyRoom: () => void; extras?: ReactNode;
}) {
  return (
    <div className="find-your-type__result">
      <ReportWithPrint data={report} />
      <ResultDestinations
        room={room} language={language}
        saved={save === "saved"} saving={save === "saving"}
        onSaveMyRoom={onSaveMyRoom}
        // Her ruling, 2026-10-17: only the Magic Room family (seals, petitions, spells) still downloads.
        onPdf={pdfAllowed(report) ? () => downloadReportPdf(report) : undefined}
        extras={extras}
      />
      {saveNote ? <p className="find-your-type__status">{saveNote}</p> : null}
    </div>
  );
}

export function DeliveredReading({ report, room, language, save, saveNote, onClose, onSaveMyRoom, extras }: {
  report: AnyReport; room: string; language: string; save: string; saveNote: string;
  onClose: () => void; onSaveMyRoom: () => void; extras?: ReactNode;
}) {
  return (
    <ClosableReport language={language} saved={save === "saved"} onClose={onClose}>
      <DeliveredReadingBody report={report} room={room} language={language} save={save}
        saveNote={saveNote} onSaveMyRoom={onSaveMyRoom} extras={extras} />
    </ClosableReport>
  );
}
